import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, LogOut } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Security() {
  const { t } = useTranslation("settings");
  const { user, logoutEverywhere } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const canSubmit =
    !!currentPassword &&
    newPassword.length >= 8 &&
    newPassword === confirmPassword &&
    newPassword !== currentPassword;

  const handleChangePassword = async () => {
    if (!user?.email || !canSubmit) return;
    setSaving(true);
    try {
      // Re-authenticate first: only the account owner, who knows the current
      // password, may change it.
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });
      if (verifyError) {
        toast.error("Your current password is not correct");
        return;
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      toast.success("Your password has been updated");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      toast.error(err.message || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  const handleSignOutEverywhere = async () => {
    setSigningOut(true);
    const result = await logoutEverywhere();
    if (!result.success) {
      setSigningOut(false);
      toast.error(t("security.signOutAll.error"));
      return;
    }
    toast.success(t("security.signOutAll.success"));
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background pb-24 mx-auto w-full max-w-4xl">
      <div className="px-6 pt-8 pb-6">
        <h1 className="text-2xl font-bold text-foreground">{t("page.security")}</h1>
        <p className="text-muted-foreground">Manage your password and access settings.</p>
      </div>

      <div className="px-6 space-y-4">
        <div className="bg-card rounded-xl border border-border p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-primary" />
            <div>
              <p className="font-medium text-foreground">Change password</p>
              <p className="text-sm text-muted-foreground">
                Only you can change your own password. Enter your current one to confirm.
              </p>
            </div>
          </div>

          <div className="space-y-3 max-w-md">
            <div>
              <Label htmlFor="current-password">Current password</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  aria-label={showCurrent ? "Hide password" : "Show password"}
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="new-password">New password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  aria-label={showNew ? "Hide password" : "Show password"}
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {newPassword && newPassword.length < 8 && (
                <p className="text-xs text-destructive mt-1">Must be at least 8 characters</p>
              )}
              {newPassword && newPassword === currentPassword && (
                <p className="text-xs text-destructive mt-1">
                  Choose a password different from your current one
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-destructive mt-1">Passwords do not match</p>
              )}
            </div>

            <Button onClick={handleChangePassword} disabled={!canSubmit || saving}>
              {saving ? "Updating..." : "Update password"}
            </Button>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <LogOut className="h-4 w-4 text-destructive" />
                <p className="font-medium text-foreground">{t("security.signOutAll.title")}</p>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{t("security.signOutAll.description")}</p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="shrink-0 text-destructive hover:text-destructive">
                  {t("security.signOutAll.button")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("security.signOutAll.confirmTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("security.signOutAll.confirmDescription")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={signingOut}>{t("security.signOutAll.cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={signingOut}
                    onClick={(event) => {
                      event.preventDefault();
                      void handleSignOutEverywhere();
                    }}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {signingOut ? t("security.signOutAll.signingOut") : t("security.signOutAll.confirm")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </div>
  );
}
